/**
 * Marketing Theme Context
 *
 * Controls light/dark theme for the public marketing site (myna-ai.co).
 * Theme setting is stored in Supabase `site_settings` table and fetched on load.
 * Only affects marketing/public pages — portals (sales, business, tech) are unaffected.
 */

import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  setTheme: async () => {},
  isLoading: true,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");
  const [isLoading, setIsLoading] = useState(true);

  // Fetch theme on mount
  useEffect(() => {
    fetchTheme();
  }, []);

  // Apply/remove light-theme class on <html> when theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") {
      root.classList.add("light-theme");
    } else {
      root.classList.remove("light-theme");
    }
  }, [theme]);

  async function fetchTheme() {
    try {
      if (!supabase) {
        setIsLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "marketing_theme")
        .single();

      if (!error && data?.value) {
        const parsed = typeof data.value === "string" ? data.value : (data.value as Record<string, string>).theme;
        if (parsed === "light" || parsed === "dark") {
          setThemeState(parsed);
        }
      }
    } catch {
      // Default to dark if fetch fails
    } finally {
      setIsLoading(false);
    }
  }

  async function setTheme(newTheme: Theme) {
    setThemeState(newTheme);

    if (!supabase) return;

    // Upsert to Supabase
    const { error } = await supabase
      .from("site_settings")
      .upsert(
        { key: "marketing_theme", value: newTheme, updated_at: new Date().toISOString() },
        { onConflict: "key" }
      );

    if (error) {
      console.error("Failed to save theme setting:", error);
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
